import { THEME_PRESETS } from '@staleless/schema'

/**
 * Pick one of four looks, and SEE it before choosing.
 *
 * §68: "Theme changes can be previewed before production." A dropdown of four
 * words does not do that — nobody knows what "Developer" means until their own
 * site is wearing it, and finding out by publishing is the wrong way round.
 *
 * So each option draws itself: a miniature with the same measure, the same
 * corner radius and the same navigation density the real site would take. The
 * shapes are CSS, not screenshots, so they cannot go stale the way a picture
 * of last year's design does.
 *
 * A miniature is the instant half of the promise. The other half is the draft
 * preview link, which builds the real site with the chosen preset while the
 * published one keeps the old look — the sentence under the picker says so,
 * because "changes appear at the next publish" is a thing people need told
 * once and remembered.
 */
export function ThemePresets({
  value,
  onChoose,
  busy,
  disabled,
}: {
  /** Null while the project has never chosen; Standard is what it renders. */
  value: string | null
  onChoose: (id: string) => void
  busy: string | null
  disabled?: boolean
}) {
  const current = value ?? 'standard'
  return (
    <div className="presets" role="radiogroup" aria-label="Theme">
      {THEME_PRESETS.map((preset) => {
        const chosen = preset.id === current
        return (
          <button
            key={preset.id}
            type="button"
            role="radio"
            aria-checked={chosen}
            className={`presets__option${chosen ? ' is-chosen' : ''}`}
            data-preset={preset.id}
            disabled={disabled || busy !== null}
            onClick={() => onChoose(preset.id)}
          >
            <span className="presets__mock" aria-hidden="true">
              <span className="presets__mock-rail">
                <i />
                <i />
                <i />
                <i />
              </span>
              <span className="presets__mock-page">
                <b />
                <i />
                <i />
                <u />
              </span>
            </span>
            <span className="presets__name">
              {preset.label}
              {value === null && preset.id === 'standard' && (
                <span className="presets__default"> · in use</span>
              )}
            </span>
            <span className="presets__audience">{preset.audience}</span>
            <span className="presets__character">{preset.character}</span>
          </button>
        )
      })}
    </div>
  )
}
